<template>
  <div class="private-fund" :class="bgColor">
    <div class="private-fund__header">
      <img
        class="private-fund__header-img"
        src="@/assets/images/private-fund/header-bg.png"
        alt=""
      />
      <FundNavBar
        v-if="!isApp"
        variant="list"
        @search="handleSearch"
        @message="handleMessage"
      />
      <div class="private-fund__header-content">
        <p class="private-fund__header-title">{{ $t("privateFund.bannerTitleLine1") }}</p>
        <p class="private-fund__header-title">{{ $t("privateFund.bannerTitleLine2") }}</p>
        <p class="private-fund__header-subtitle">
          <span class="private-fund__header-subtitle-line" aria-hidden="true" />
          <span>{{ $t("privateFund.bannerSubtitle") }}</span>
        </p>
      </div>
    </div>

    <div ref="listScroll" class="private-fund__body">
      <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
        <div v-if="showEmpty" class="private-fund__empty">
          <img
            class="private-fund__empty-icon"
            src="@/assets/images/app-pages/recordEmpty.png"
            alt=""
          />
          <span class="private-fund__empty-text">{{ $t("public.contentText[1]") }}</span>
        </div>
        <van-list
          v-else
          v-model="loading"
          :finished="finished"
          :immediate-check="false"
          offset="80"
          finished-text=""
          @load="onLoad"
        >
          <div
            v-for="item in list"
            :key="item.id"
            class="private-fund__card"
          >
            <div class="private-fund__card-head">
              <p class="private-fund__card-name">{{ item.name }}</p>
              <button
                type="button"
                class="private-fund__card-btn"
                @click="handleView(item.id)"
              >
                {{ $t("privateFund.list.view") }}
              </button>
            </div>

            <div class="private-fund__metrics">
              <div class="private-fund__metric is-aum">
                <p class="private-fund__metric-value">{{ item.assetsUnderManagement }}</p>
                <p class="private-fund__metric-label is-aum-label">{{ getAssetsUnderManagementLabel(item) }}</p>
              </div>
              <div class="private-fund__metric is-return">
                <p class="private-fund__metric-value" :class="getReturnTrendClass(item.returnYtd)">{{ item.returnYtd }}</p>
                <p class="private-fund__metric-label is-return-nowrap">{{ $t("privateFund.list.returnYtd") }}</p>
              </div>
              <div class="private-fund__metric is-return">
                <p class="private-fund__metric-value" :class="getReturnTrendClass(item.returnSinceInception)">{{ item.returnSinceInception }}</p>
                <p
                  class="private-fund__metric-label"
                  :class="isEn ? 'is-return-label' : 'is-return-nowrap'"
                >{{ $t("privateFund.list.returnSinceInception") }}</p>
              </div>
            </div>

            <FundNavChartChrome
              v-if="item.navList.length"
              class="private-fund__card-chart"
            >
              <FundNavChart
                :nav-list="item.navList"
                :chart-key="item.id"
              />
            </FundNavChartChrome>

            <p v-if="item.strategy" class="private-fund__strategy">{{ $t("privateFund.strategyPrefix") }}{{ item.strategy }}</p>
          </div>
        </van-list>
        <DisclaimerFooterLink v-if="list.length > 0" />
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import { List, PullRefresh } from "vant";
import { getBrowser } from "@/utils/browser";
import { getAppToken } from "@/utils/hybirdMethods";
import {
  getReturnTrendClass,
  mapFundListItem,
  resolveAssetsUnderManagementLabel
} from "../utils";
import FundNavBar from "../components/FundNavBar.vue";
import FundNavChart from "../components/FundNavChart.vue";
import FundNavChartChrome from "../components/FundNavChartChrome.vue";
import DisclaimerFooterLink from "../components/DisclaimerFooterLink.vue";

const browser = getBrowser();

const PAGE_SIZE = 10;

export default {
  name: "PrivateFundList",
  components: {
    [List.name]: List,
    [PullRefresh.name]: PullRefresh,
    FundNavBar,
    FundNavChart,
    FundNavChartChrome,
    DisclaimerFooterLink
  },
  data() {
    return {
      isApp: browser.isApp,
      list: [],
      loading: false,
      refreshing: false,
      finished: false,
      page: 0,
      ready: false
    };
  },
  async created() {
    await this.initAppToken();
    this.ready = true;
    this.onLoad();
  },
  computed: {
    bgColor() {
      return this.$store.getters["common/bgColor"];
    },
    showEmpty() {
      return this.ready && !this.loading && this.finished && this.list.length === 0;
    },
    isEn() {
      return this.$i18n.locale === "en";
    }
  },
  methods: {
    getReturnTrendClass,
    getAssetsUnderManagementLabel(item) {
      return resolveAssetsUnderManagementLabel(
        this.$t.bind(this),
        item.assetsUnderManagementUnit,
        item.assetsUnderManagementUnitDisplay
      );
    },
    async initAppToken() {
      if (!browser.isApp) {
        return;
      }
      try {
        const res = await getAppToken();
        if (res === "isGetPro" || !res) {
          return;
        }
        const userInfo = JSON.parse(res);
        if (userInfo?.token) {
          localStorage.setItem("token", userInfo.token);
          sessionStorage.setItem("token", userInfo.token);
        }
      } catch (error) {
        console.log("[private-fund/list] 获取 App token 失败:", error);
      }
    },
    async onLoad() {
      if (!this.ready || this.finished) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.page += 1;
      try {
        const res = await this.$apis.getFundProductList({
          page: this.page,
          page_size: PAGE_SIZE
        });
        const rows = (res?.data || []).map(mapFundListItem);
        this.list = this.list.concat(rows);
        const total = Number(res?.total) || 0;
        const perPage = Number(res?.per_page) || PAGE_SIZE;
        this.finished =
          rows.length === 0 ||
          this.list.length >= total ||
          this.page * perPage >= total;
      } catch (error) {
        console.log("[private-fund/list] 获取基金列表失败:", error);
        this.finished = true;
      } finally {
        this.loading = false;
      }
    },
    async onRefresh() {
      this.page = 0;
      this.list = [];
      this.finished = false;
      try {
        await this.onLoad();
      } finally {
        this.refreshing = false;
      }
    },
    handleView(id) {
      this.toDetail(id);
    },
    toDetail(id) {
      const routeLocation = {
        path: "/private-fund/fundDetails",
        query: {
          ...this.$route.query,
          id
        }
      };

      if (browser.isApp) {
        const { href } = this.$router.resolve(routeLocation);
        this.$JsBridge.callHandler("jumpPage", {
          type: 1,
          url: `${window.location.origin}${href}`
        });
        return;
      }

      this.$router.push(routeLocation);
    },
    handleSearch() {
      // TODO: 接入搜索页
    },
    handleMessage() {
      // TODO: 接入消息页
    }
  }
};
</script>

<style scoped lang="less">
.private-fund {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #f3f4f6;

  &__header {
    position: relative;
    flex-shrink: 0;
    width: 100%;
    overflow: hidden;

    @media (min-width: 768px) {
      height: 380px;
    }
  }

  &__header-img {
    display: block;
    width: 100%;
    height: auto;

    @media (min-width: 768px) {
      height: 100%;
      object-fit: cover;
      object-position: center top;
    }
  }

  &__header-content {
    position: absolute;
    left: 32px;
    bottom: 60px;
    max-width: 90%;
  }

  &__header-title {
    color: #fff;
    font-size: 40px;
    font-weight: 600;
    line-height: 56px;
  }

  &__header-subtitle {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.92);
    font-size: 24px;
    line-height: 34px;
  }

  &__header-subtitle-line {
    flex-shrink: 0;
    width: 40px;
    height: 2px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 1px;
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 24px 24px 32px;

    /deep/ .van-pull-refresh {
      min-height: 100%;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 150px 0;
  }

  &__empty-icon {
    width: 246px;
    height: 248px;
  }

  &__empty-text {
    margin-top: 14px;
    color: #747377;
    font-size: 28px;
    line-height: 40px;
  }

  &__card {
    margin-bottom: 24px;
    padding: 32px 28px;
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  &__card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }

  &__card-name {
    flex: 1;
    min-width: 0;
    color: #171717;
    font-size: 34px;
    font-weight: 600;
    line-height: 48px;
    word-break: break-word;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__card-btn {
    flex-shrink: 0;
    min-width: 112px;
    height: 56px;
    padding: 0 24px;
    border: none;
    border-radius: 28px;
    background: #171832;
    color: #fff;
    font-size: 26px;
    line-height: 56px;
  }

  &__metrics {
    display: flex;
    align-items: flex-start;
    gap: 48px;
    margin-top: 28px;
  }

  &__metric {
    min-width: 0;

    &.is-aum {
      flex: 1 1 0;
      min-width: 0;
    }

    &.is-return {
      flex: 0 0 auto;
    }
  }

  &__metric-value {
    color: #171717;
    font-size: 40px;
    font-weight: 600;
    line-height: 56px;
    min-height: 56px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.is-rise {
      color: #e83324;
    }

    &.is-fall {
      color: #02aa39;
    }

    &.is-flat {
      color: #999;
    }
  }

  &__metric-label {
    margin-top: 4px;
    color: #999;
    font-size: 22px;
    line-height: 32px;

    &.is-aum-label {
      word-break: break-word;
    }

    &.is-return-label {
      white-space: pre-line;
    }

    &.is-return-nowrap {
      white-space: nowrap;
    }
  }

  &__card-chart {
    margin-top: 24px;
  }

  &__strategy {
    margin-top: 24px;
    color: #666;
    font-size: 24px;
    line-height: 38px;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  &.black-theme {
    background: #0a0b0f;

    .private-fund__card {
      background: #1b1c24;
      box-shadow: none;
    }

    .private-fund__card-name {
      color: #fff;
    }

    .private-fund__card-btn {
      background: #377cfb;
    }

    .private-fund__metric-value {
      color: #fff;

      &.is-rise {
        color: #e83324;
      }

      &.is-fall {
        color: #02aa39;
      }

      &.is-flat {
        color: #9d9d9f;
      }
    }

    .private-fund__metric-label,
    .private-fund__strategy {
      color: #9d9d9f;
    }

    .private-fund__empty-text {
      color: #9d9d9f;
    }

    /deep/ .van-list__finished-text {
      color: #9d9d9f;
    }
  }
}
</style>
